package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.DataExport
import com.evoapps.evoshift.domain.model.ExportLimitReachedException
import com.evoapps.evoshift.domain.model.RecentSignInRequiredException
import com.evoapps.evoshift.domain.usecase.auth.SignOutUseCase
import com.evoapps.evoshift.domain.usecase.user.ExportMyDataUseCase
import com.evoapps.evoshift.domain.usecase.user.ExportWorkplaceDataUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.export_limit_reached
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** What to export: the signed-in person's data, or a workplace's (owners and managers). */
data class ExportTarget(val workplaceId: String? = null)

data class DataExportUiState(
    val isWorking: Boolean = false,
    /** The archive is ready: the screen offers the link (opened on the person's tap). */
    val ready: DataExport? = null,
    /** The server wants a fresh sign-in (10 minutes) before handing out the data. */
    val needsSignIn: Boolean = false,
    val error: UiText? = null
)

/** "Download my data" / "Export workplace data" (SH-27): asks the server, then offers the link. */
class DataExportViewModel(
    private val target: ExportTarget,
    private val exportMyData: ExportMyDataUseCase,
    private val exportWorkplace: ExportWorkplaceDataUseCase,
    private val signOut: SignOutUseCase
) : ViewModel() {
    private val _state = MutableStateFlow(DataExportUiState())
    val state: StateFlow<DataExportUiState> = _state.asStateFlow()

    fun export() {
        if (_state.value.isWorking) return
        _state.update { DataExportUiState(isWorking = true) }
        viewModelScope.launch {
            val result = target.workplaceId?.let { exportWorkplace(it) } ?: exportMyData()
            result.fold(
                onSuccess = { export -> _state.update { DataExportUiState(ready = export) } },
                onFailure = { e ->
                    _state.update {
                        when (e) {
                            is RecentSignInRequiredException -> DataExportUiState(needsSignIn = true)
                            is ExportLimitReachedException -> DataExportUiState(error = UiText.Resource(Res.string.export_limit_reached))
                            else -> DataExportUiState(error = e.toUiText())
                        }
                    }
                }
            )
        }
    }

    /** Signing in again is the way to a fresh sign-in; the app returns to the login screen. */
    fun signOutToSignInAgain() {
        _state.update { DataExportUiState() }
        viewModelScope.launch { runCatching { signOut() } }
    }

    fun dismiss() {
        if (!_state.value.isWorking) _state.update { DataExportUiState() }
    }
}
